// Shared finance UI primitives — table-cell class constants, the selected-pill
// token, the available-funds accent threshold, the validated Remaining Balance
// headline card, a status-dot title helper, and the standard fetch-error banner.
//
// These were copy-pasted (with drift) across ~7 finance pages; this module is the
// single source of truth so they can never diverge again. Behavior is identical
// to the previous local copies — no numbers or thresholds change here.

import type { ReactNode } from "react";
import KPICard from "../dashboard/KPICard";
import InfoTip from "../shared/InfoTip";
import Banner from "../shared/Banner";
import { usd0 } from "./charts";

// ── Table cell class constants ────────────────────────────────────────────────
// Canonical finance-table header / data cell classes. Two header paddings were in
// use (px-3 py-2.5 and px-4 py-3); both are exported so each table keeps its
// existing density. `TH`/`THR`/`TD`/`TDR` default to the px-3 set (the most
// common); the `*_LG` variants are the px-4 set used by the Levy page tables.
export const TH =
  "px-3 py-2.5 text-xs font-semibold text-theme-muted uppercase tracking-wider";
export const THR = `${TH} text-right`;
export const TD = "px-3 py-2.5 text-sm text-theme-secondary";
export const TDR = `${TD} text-right tabular-nums`;

// px-4 py-3 density (Levy Funds / Levy Position tables).
export const TH_LG =
  "px-4 py-3 text-xs font-semibold text-theme-muted uppercase tracking-wider";
export const THR_LG = `${TH_LG} text-right`;

// ── Selected-pill token ───────────────────────────────────────────────────────
// The DCVFD-brand dark-green used for the active state of toggle pills / segmented
// controls across finance (was a raw `bg-[#0E403A]` literal in ~4 places).
export const PILL_ACTIVE = "bg-[#0E403A] text-white";

// Solid brand button (primary action) — same DCVFD dark-green with a hover
// darken. Was a raw `bg-[#0E403A] … hover:bg-[#0E403A]/90` literal in a couple
// of finance action buttons.
export const BTN_BRAND =
  "bg-[#0E403A] text-white hover:bg-[#0E403A]/90 transition-colors";

// ── Funds-available accent threshold ──────────────────────────────────────────
// < $100K → red, < $300K → amber, else green. The single definition for the
// Remaining Balance / Discretionary Available KPI accents (and the verdict tone).
export function availableAccent(n: number): "green" | "amber" | "red" {
  if (n < 100_000) return "red";
  if (n < 300_000) return "amber";
  return "green";
}

// ── Remaining Balance headline card ───────────────────────────────────────────
// Shows the budgetary-control Funds Available (all objects) — the figure that
// ties to Oracle. Duplicated verbatim in FinanceOverview + Finance; one copy now.
export function RemainingBalanceCard({
  remaining,
  asOf,
  isLoading,
}: {
  remaining: number;
  asOf: string | null;
  isLoading: boolean;
}) {
  const tip =
    `Funds available${asOf ? ` (as of ${asOf})` : ""}: ${usd0(remaining)}. ` +
    `Budgetary-control figure across all objects (nets in budgeted-but-uncollected revenue). Ties to Oracle's Funds Available.`;
  return (
    <div title={tip}>
      <KPICard
        label={
          <span className="inline-flex items-center gap-1">
            Remaining Balance <InfoTip term="funds_available" />
          </span>
        }
        value={usd0(remaining)}
        subtitle="Funds available (budgetary control · all objects)"
        accent={availableAccent(remaining)}
        isLoading={isLoading}
      />
    </div>
  );
}

// ── Status-dot title ──────────────────────────────────────────────────────────
// Screen-reader-safe replacement for emoji-prefixed ChartCard titles. Renders a
// small colored dot (with an sr-only label) followed by the title text, matching
// the status-dot idiom used elsewhere (signal cards, StatusLegend).
const DOT_CLASS: Record<"red" | "amber" | "green", string> = {
  red: "bg-status-red",
  amber: "bg-status-amber",
  green: "bg-status-green",
};
const DOT_LABEL: Record<"red" | "amber" | "green", string> = {
  red: "Act now",
  amber: "Watch",
  green: "OK",
};

export function StatusTitle({
  tone,
  children,
}: {
  tone: "red" | "amber" | "green";
  children: ReactNode;
}) {
  return (
    <span className="inline-flex items-center gap-2">
      <span
        className={`inline-block h-2.5 w-2.5 rounded-full shrink-0 ${DOT_CLASS[tone]}`}
        aria-hidden="true"
      />
      <span className="sr-only">{DOT_LABEL[tone]}: </span>
      {children}
    </span>
  );
}

// ── Fetch-error banner ────────────────────────────────────────────────────────
// Standard "couldn't load this data" surface for a finance page whose primary
// polling hook returned an error (a failed/403 load otherwise looks like "no
// data"). Renders nothing when there is no error.
export function FetchErrorBanner({
  error,
  what = "this data",
  className,
}: {
  error: string | null | undefined;
  what?: string;
  className?: string;
}) {
  if (!error) return null;
  return (
    <Banner
      variant="error"
      title={`Couldn't load ${what}`}
      className={className}
    >
      The dashboard hit an error fetching from the server ({error}). The figures
      below may be stale or missing. Try refreshing; if it persists, you may
      need to sign in again or the data feed may be down.
    </Banner>
  );
}
